import { authAxios } from '../utils/session_utils';
import { makeKeyInLowerCaseHelper, LowerCaseKeyInterFace } from '../utils';
import { PlayoutStatus, YouTubeLiveStream, YouTubeLiveStreamBroadcast, YouTubeOAuthToken, ScheduleChunk } from './types';

// Thin API wrapper around YouTube live backend endpoints.
export async function listStreams(): Promise<YouTubeLiveStream[]> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams`
  });

  const converted: LowerCaseKeyInterFace[] = await makeKeyInLowerCaseHelper(response.data.streams);
  return converted as YouTubeLiveStream[];
}

export interface AsPlayedQuery {
  pagination: { current: number; pageSize: number };
  sortObj?: { column: string; order: 'asc' | 'desc' };
  filter?: {
    stream_ids?: string[];
    start_date?: string | null;
    end_date?: string | null;
    type?: string | null;
  };
}
 
export async function listAsPlayed(
	query: AsPlayedQuery
): Promise<{ data: LowerCaseKeyInterFace[]; total: number }> {
	const params = new URLSearchParams();
	params.set("pagination", JSON.stringify(query.pagination));
	if (query.sortObj) params.set("sortObj", JSON.stringify(query.sortObj));
	if (query.filter) params.set("filter", JSON.stringify(query.filter));

	const response = await authAxios({
		method: "GET",
		url: `${process.env.REACT_APP_BACK_END_API}/snowflake/as-played?${params.toString()}`,
	});

	const data = await makeKeyInLowerCaseHelper(response.data?.data ?? []);
	return { data, total: response.data?.total ?? 0 };
}

/**
 * Fetch every row matching `filter` by paging the list endpoint (pageSize is
 * capped at 100 server-side, so we loop). Newest-first to match the page.
 * Used by the client-side CSV export — "whatever is currently filtered".
 * `onProgress` reports (fetched, total) so the UI can show progress.
 */
export async function fetchAllAsPlayed(
	filter: AsPlayedQuery["filter"],
	onProgress?: (fetched: number, total: number) => void
): Promise<LowerCaseKeyInterFace[]> {
	const pageSize = 100;
	const all: LowerCaseKeyInterFace[] = [];
	let current = 1;
	let total = Infinity;

	while (all.length < total) {
		const { data, total: t } = await listAsPlayed({
			pagination: { current, pageSize },
			sortObj: { column: "started_at", order: "desc" },
			filter,
		});
		total = t;
		all.push(...data);
		onProgress?.(all.length, total);
		// Safety: stop if a page comes back empty (e.g. total is 0 or drifted).
		if (data.length === 0) break;
		current += 1;
	}

	return all;
}

export async function getStreamWithVideos(streamId: string): Promise<YouTubeLiveStream> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}`
  });

  const converted: any[] = await makeKeyInLowerCaseHelper([response.data.stream]);
  converted[0].videos = await makeKeyInLowerCaseHelper(response.data.stream.videos);
  return converted[0] as YouTubeLiveStream;
}

export async function deleteStream(streamId: string): Promise<void> {
  await authAxios({
    method: 'DELETE',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}`
  });
}

/** Hide a stream from the default list. The BE refuses a stream that is on air. */
export async function archiveStream(streamId: string): Promise<void> {
  await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/archive`
  });
}

export async function unarchiveStream(streamId: string): Promise<void> {
  await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/unarchive`
  });
}

export async function cancelStream(streamId: string): Promise<void> {
  await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/cancel`
  });
}

export async function fetchPlayoutStatus(streamId: string): Promise<PlayoutStatus> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/playout-status`
  });

  return response.data.playout_status;
}

export async function startPlayout(streamId: string, startFromMinute: number, timeoutMs?: number): Promise<void> {
  await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/start-playout`,
    data: { start_from_minute: startFromMinute },
    timeout: timeoutMs
  });
}

export async function stopPlayout(streamId: string): Promise<void> {
  await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/stop-playout`
  });
}

export async function transcodeStream(streamId: string) {
  const response = await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/transcode`
  });

  return response.data;
}

export async function listBroadcasts(streamId: string): Promise<YouTubeLiveStreamBroadcast[]> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/broadcasts`
  });

  const converted: any[] = await makeKeyInLowerCaseHelper(response.data.broadcasts || []);
  return converted as YouTubeLiveStreamBroadcast[];
}

export async function refreshBroadcasts(streamId: string): Promise<YouTubeLiveStreamBroadcast[]> {
  const response = await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/broadcasts/refresh`
  });

  const converted: any[] = await makeKeyInLowerCaseHelper(response.data.broadcasts || []);
  return converted as YouTubeLiveStreamBroadcast[];
}

export async function refreshStreamHealth(streamId: string): Promise<YouTubeLiveStream> {
  const response = await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/stream-health/refresh`
  });

  const converted: any[] = await makeKeyInLowerCaseHelper([response.data.stream]);
  return converted[0] as YouTubeLiveStream;
}

export async function transitionBroadcastLive(streamId: string): Promise<void> {
  await authAxios({
    method: 'POST',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/broadcasts/transition-live`
  });
}

// OAuth token operations

export async function listOAuthTokens(): Promise<YouTubeOAuthToken[]> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/oauth-tokens`
  });
  const converted = await makeKeyInLowerCaseHelper(response.data.tokens);
  return converted as YouTubeOAuthToken[];
}

export async function deleteOAuthToken(tokenId: string): Promise<void> {
  await authAxios({
    method: 'DELETE',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/oauth-tokens/${tokenId}`
  });
}

export async function getOAuthAuthUrl(userId: string): Promise<string> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/auth`,
    params: { user_id: userId }
  });
  return response.data.authUrl;
}

export async function refreshTokenChannelInfo(tokenId: string): Promise<{ channelId: string; channelTitle: string }> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/channel-id`,
    params: { token_id: tokenId }
  });
  // Backend returns { channelId: { id, title, country } }
  const channelData = response.data.channelId;
  return {
    channelId: typeof channelData === 'object' ? channelData.id : channelData,
    channelTitle: typeof channelData === 'object' ? channelData.title : '',
  };
}

// Schedule chunks (Amagi HLS-pull streams): imported Marathon FAST CSVs that
// queue as monthly chunks on a persistent stream.

export interface ImportChunkResult {
  chunk_id: string;
  row_count: number;
  first_schedule_time: string | null;
  last_schedule_time: string | null;
  timezone: string | null;
}

/**
 * Upload a schedule CSV as a new queued chunk. On a 400 the backend returns
 * ALL validation problems at once in `errors` — rethrown as
 * `ScheduleImportError` so the UI can list them.
 */
export class ScheduleImportError extends Error {
  errors: string[];
  constructor(message: string, errors: string[]) {
    super(message);
    this.errors = errors;
  }
}

export async function importScheduleChunk(streamId: string, file: File | Blob): Promise<ImportChunkResult> {
  const formData = new FormData();
  formData.append('schedule_csv', file);
  try {
    const response = await authAxios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/schedule-chunks`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data as ImportChunkResult;
  } catch (error: any) {
    const data = error?.response?.data;
    throw new ScheduleImportError(
      data?.error || 'Failed to import schedule CSV',
      Array.isArray(data?.errors) ? data.errors : []
    );
  }
}

export async function listScheduleChunks(streamId: string): Promise<ScheduleChunk[]> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/schedule-chunks`
  });
  return (response.data.chunks ?? []) as ScheduleChunk[];
}

export async function deleteScheduleChunk(streamId: string, chunkId: string): Promise<void> {
  await authAxios({
    method: 'DELETE',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/schedule-chunks/${encodeURIComponent(chunkId)}`
  });
}

/**
 * Get a short-lived presigned URL for the backed-up original CSV of a chunk.
 * The URL carries a content-disposition header, so opening it downloads the
 * file under its original name. The backend answers 404 when no backup exists.
 */
export async function getScheduleChunkDownloadUrl(
  streamId: string,
  chunkId: string
): Promise<{ url: string; filename: string; expires_in: number }> {
  const response = await authAxios({
    method: 'GET',
    url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/schedule-chunks/${encodeURIComponent(chunkId)}/download`
  });
  return response.data as { url: string; filename: string; expires_in: number };
}
